/**
 * Is the scoreboard on for this person, and are they a leader? Pure classification of the
 * server's answers — the store in `useScoreboard.ts` holds the state.
 *
 * E13: every scoreboard read answers 404 `SCOREBOARD_DISABLED` while the tenant has it off, and a
 * tenant without it must see the app exactly as before — so "disabled" (and anything that is not a
 * clear "on") renders nothing new.
 */

export type ScoreboardFailure = 'DISABLED' | 'NOT_LEADER' | 'OUT_OF_SCOPE' | 'NOT_SALES' | 'UNAVAILABLE' | 'ERROR'

export function classifyFailure(status: number | undefined, errorCode: string | null | undefined): ScoreboardFailure {
  switch (errorCode) {
    case 'SCOREBOARD_DISABLED': return 'DISABLED'
    case 'SCOREBOARD_LEADER_REQUIRED': return 'NOT_LEADER'
    case 'SCOREBOARD_OUT_OF_SCOPE': return 'OUT_OF_SCOPE'
    case 'SCOREBOARD_SALES_USER_REQUIRED': return 'NOT_SALES'
    case 'SCOREBOARD_TIMEZONE_UNAVAILABLE': return 'UNAVAILABLE'
    default: break
  }
  // A gateway / server that predates the scoreboard has no route at all: a plain 404 is "off" too.
  if (status === 404) return 'DISABLED'
  if (status === 503) return 'UNAVAILABLE'
  return 'ERROR'
}

/** A failure that means "this screen should not exist for you" → send them home, show nothing. */
export function isHideFailure(f: ScoreboardFailure | null | undefined): boolean {
  return f === 'DISABLED' || f === 'NOT_LEADER' || f === 'NOT_SALES' || f === 'OUT_OF_SCOPE'
}

export type AccessState =
  | { kind: 'unknown' }
  | { kind: 'off' }
  | { kind: 'on'; isLeader: boolean }

/**
 * The scoreboard is "on" for someone when `/me` answered. Leader = the server's own manager rule
 * on their row (the same `isManagerUser` the team endpoint gates on), or the Lead Pool's pulse,
 * which is computed by the same rule — whichever is known.
 */
export function accessFrom(
  me: { row?: { isManager?: boolean | null } | null } | null,
  failure: ScoreboardFailure | null,
  pulseIsManager?: boolean | null,
): AccessState {
  if (me) return { kind: 'on', isLeader: me.row?.isManager === true || pulseIsManager === true }
  if (failure) return { kind: 'off' }
  return { kind: 'unknown' }
}

/**
 * Where /sales/home sends someone. Leaders land on Team today (P2.1) — unless they asked for
 * their own day (`?mine=1`) or a notification is opening the day report on My day.
 */
export function homeRedirect(access: AccessState, params: URLSearchParams): string | null {
  if (access.kind !== 'on' || !access.isLeader) return null
  if (params.has('mine') || params.has('dayReport')) return null
  return '/sales/team'
}
